import { FC, useCallback, useRef, useState } from 'react';

import { ScrollableContainer } from '@hh.ru/magritte-ui-scrollable-container';
import { Tab } from '@hh.ru/magritte-ui-tabs/Tab';
import { type TabsProps } from '@hh.ru/magritte-ui-tabs/types';
import { useTabs } from '@hh.ru/magritte-ui-tabs/useTabs';

import styles from './tabs.less';

const defaultContainerCorrections = {};

export const Tabs: FC<TabsProps> = ({
    children,
    activeItemId,
    onChange,
    'data-qa': dataQa = 'tab-list',
    containerCorrections = defaultContainerCorrections,
    disableContainerButtons,
    containerControlsRef,
    onScroll,
    mode = 'primary',
    ...tabsProps
}) => {
    const [tabsContainer, setTabsContainer] = useState<HTMLDivElement | null>(null);
    const indicatorRef = useRef<HTMLDivElement | null>(null);

    const tabs = useTabs({
        activeItemId,
        indicatorRef,
        items: children,
        onChange,
        tabsContainerRef: { current: tabsContainer },
    });

    const setRefElement = useCallback(
        (element: HTMLDivElement | null) => {
            if (element && !tabsContainer) {
                setTabsContainer(element);
            }
        },
        [tabsContainer]
    );

    if (!tabs?.length) {
        return null;
    }

    return (
        <ScrollableContainer
            {...containerCorrections}
            disableButtons={disableContainerButtons}
            ref={containerControlsRef}
            onScroll={onScroll}
        >
            <div {...tabsProps} className={styles.tabs} data-qa={dataQa} role="tablist" ref={setRefElement}>
                {tabs.map((item) => (
                    <Tab key={item.id} {...item} mode={mode} />
                ))}
            </div>
            {mode === 'secondary' && (
                <div className={styles.indicatorWrapper}>
                    <div className={styles.indicator} ref={indicatorRef} data-qa="active-tab-indicator" />
                </div>
            )}
        </ScrollableContainer>
    );
};
